/**
 * Admin Students Management Tab Component
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { adminStudentsAPI, reportsAPI, downloadCSV } from '@/lib/api';
import { BulkStudentUpload } from '@/components/BulkStudentUpload';
import { Plus, User, StickyNote, Loader2, UserPlus, Search, X, Trash2, AlertTriangle, GraduationCap, BookOpen, Download, DollarSign } from 'lucide-react';
import { coursesAPI } from '@/lib/api';
import api from '@/lib/api';

export const StudentsTab = ({ students, onRefresh, onViewStudent, isSuperAdmin }) => {
  const navigate = useNavigate();
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'agent' | 'direct'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedStudentForApply, setSelectedStudentForApply] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [courseSearchResults, setCourseSearchResults] = useState([]);
  const [searchingCourses, setSearchingCourses] = useState(false);
  const courseSearchTimer = useRef(null);
  const [applyingForCourse, setApplyingForCourse] = useState(null);
  // Phase C — Admin Record Payment dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStudent, setPaymentStudent] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_type: 'tuition', description: '', payment_date: '',
    reference_number: '', status: 'completed', notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  const openPaymentDialog = async (student) => {
    setPaymentStudent(student);
    setPaymentForm({ amount: '', payment_type: 'tuition', description: '', payment_date: '', reference_number: '', status: 'completed', notes: '' });
    setShowPaymentDialog(true);
    try {
      const { data } = await api.get(`/admin/student/${student.id}/payments`);
      setPaymentHistory(data.payments || []);
    } catch (e) {
      setPaymentHistory([]);
    }
  };

  const savePayment = async () => {
    if (!paymentStudent || !paymentForm.amount || !paymentForm.description) {
      toast.error('Amount and description are required');
      return;
    }
    setSavingPayment(true);
    try {
      await api.post(`/admin/student/${paymentStudent.id}/payments`, {
        ...paymentForm,
        amount: Number(paymentForm.amount),
        payment_date: paymentForm.payment_date || undefined,
      });
      toast.success('Payment recorded');
      const { data } = await api.get(`/admin/student/${paymentStudent.id}/payments`);
      setPaymentHistory(data.payments || []);
      setPaymentForm({ amount: '', payment_type: 'tuition', description: '', payment_date: '', reference_number: '', status: 'completed', notes: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };
  const [newStudent, setNewStudent] = useState({
    full_name: '',
    email: '',
    phone: '',
    source: 'website',
    source_detail: '',
    education_level: '',
    preferred_countries: [],
    field_of_study: '',
    notes: ''
  });

  // Fetch courses when Apply dialog opens
  const handleOpenApplyDialog = async (student) => {
    setSelectedStudentForApply(student);
    setShowApplyDialog(true);
    try {
      const res = await coursesAPI.getAll();
      setCourses(res.data);
      setCourseSearchResults((Array.isArray(res.data) ? res.data : []).slice(0, 50));
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses');
    }
  };

  // Apply for course
  const handleApplyForStudent = async (courseId) => {
    if (!selectedStudentForApply) return;
    setApplyingForCourse(courseId);
    try {
      await adminStudentsAPI.applyForStudent(selectedStudentForApply.id, courseId);
      toast.success('Application submitted successfully!');
      setShowApplyDialog(false);
      setSelectedStudentForApply(null);
      setCourseSearchTerm('');
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplyingForCourse(null);
    }
  };

  // Filter courses
  // Debounced server-side course search
  const searchCoursesFromServer = useCallback(async (term) => {
    if (!term.trim()) {
      setCourseSearchResults(courses.slice(0, 50));
      setSearchingCourses(false);
      return;
    }
    setSearchingCourses(true);
    try {
      const res = await coursesAPI.search(term);
      setCourseSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error searching courses:', error);
    } finally {
      setSearchingCourses(false);
    }
  }, [courses]);

  useEffect(() => {
    if (courseSearchTimer.current) clearTimeout(courseSearchTimer.current);
    if (!courseSearchTerm.trim()) {
      setCourseSearchResults(courses.slice(0, 50));
      return;
    }
    setSearchingCourses(true);
    courseSearchTimer.current = setTimeout(() => {
      searchCoursesFromServer(courseSearchTerm);
    }, 350);
    return () => { if (courseSearchTimer.current) clearTimeout(courseSearchTimer.current); };
  }, [courseSearchTerm, searchCoursesFromServer]);

  const filteredCourses = courseSearchResults;

  // Helper: is the student agent-sourced or direct?
  const isAgentSourced = (s) => Boolean(
    s?.agent_company_name || s?.referred_by_agent || s?.agent_id || s?.agent_managed || s?.has_agent
  );

  // Filter students based on search query + source-type chip
  const filteredStudents = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return students.filter(student => {
      if (sourceFilter === 'agent' && !isAgentSourced(student)) return false;
      if (sourceFilter === 'direct' && isAgentSourced(student)) return false;
      if (!query) return true;
      return (
        student.full_name?.toLowerCase().includes(query) ||
        student.email?.toLowerCase().includes(query) ||
        student.id?.toLowerCase().includes(query) ||
        student.phone?.includes(query) ||
        student.reference_code?.toLowerCase().includes(query)
      );
    });
  }, [students, searchQuery, sourceFilter]);

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (!newStudent.full_name || !newStudent.email) {
      toast.error('Name and email are required');
      return;
    }
    setAddingStudent(true);
    try {
      const response = await adminStudentsAPI.create(newStudent);
      toast.success(`Student created! Temporary password: ${response.data.temp_password}`);
      setShowAddStudent(false);
      setNewStudent({
        full_name: '',
        email: '',
        phone: '',
        source: 'website',
        source_detail: '',
        education_level: '',
        preferred_countries: [],
        field_of_study: '',
        notes: ''
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create student');
    } finally {
      setAddingStudent(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!deletingStudent) return;
    setIsDeleting(true);
    try {
      await adminStudentsAPI.delete(deletingStudent.id);
      toast.success(`Student "${deletingStudent.full_name}" deleted successfully`);
      setShowDeleteConfirm(false);
      setDeletingStudent(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirm = (student) => {
    setDeletingStudent(student);
    setShowDeleteConfirm(true);
  };

  return (
    <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">All Students</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredStudents.length === students.length 
              ? `${students.length} registered students` 
              : `Showing ${filteredStudents.length} of ${students.length} students`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by ref code, name, email, ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 w-full sm:w-64 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
              data-testid="student-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <BulkStudentUpload 
            onUpload={(students) => adminStudentsAPI.bulkUpload(students)}
            isAgent={false}
          />
          <Button
            variant="outline"
            onClick={async () => {
              try {
                toast.info('Generating students report...');
                await downloadCSV(reportsAPI.exportStudents(), `students_report_${new Date().toISOString().slice(0,10)}.csv`);
                toast.success('Students report downloaded');
              } catch (err) {
                const msg = err?.message || 'Failed to download report';
                toast.error(msg);
              }
            }}
            className="gap-2"
            data-testid="download-students-csv"
          >
            <Download className="w-4 h-4" />
            <span className="hidden lg:inline">Export CSV</span>
          </Button>
          <Button
            onClick={() => setShowAddStudent(true)}
            className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
            data-testid="add-student-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Student
          </Button>
        </div>
      </div>

      {/* Source-type filter chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="student-source-filter">
        {[
          { key: 'all', label: 'All', count: students.length },
          { key: 'agent', label: 'Agent-sourced', count: students.filter(isAgentSourced).length },
          { key: 'direct', label: 'Direct', count: students.filter((s) => !isAgentSourced(s)).length },
        ].map((chip) => (
          <button
            key={chip.key}
            onClick={() => setSourceFilter(chip.key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${
              sourceFilter === chip.key
                ? chip.key === 'agent'
                  ? 'bg-purple-600 border-purple-600 text-white'
                  : chip.key === 'direct'
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-gray-800 border-gray-800 text-white dark:bg-white dark:text-gray-900'
                : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600'
            }`}
            data-testid={`student-source-filter-${chip.key}`}
          >
            {chip.label} <span className="ml-1 opacity-70">({chip.count})</span>
          </button>
        ))}
      </div>

      {/* Students Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-slate-600">
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Ref Code</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Student</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Contact</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Source</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Applications</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Notes</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Joined</th>
              <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-8 text-center text-gray-500 dark:text-gray-400">
                  {searchQuery ? `No students found matching "${searchQuery}"` : 'No students registered yet'}
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50" data-testid={`admin-student-${student.id}`}>
                  <td className="py-3 px-4">
                    <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {student.reference_code || `${student.id?.slice(0, 8)}…`}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 dark:text-white">{student.full_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{student.email}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{student.phone || 'No phone'}</p>
                  </td>
                  <td className="py-3 px-4">
                    {isAgentSourced(student) ? (
                      <div data-testid={`student-source-agent-${student.id}`}>
                        <Badge className="bg-purple-100 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800">
                          Agent
                        </Badge>
                        {student.resolved_agent_id ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin?tab=agents&agentId=${student.resolved_agent_id}`);
                            }}
                            className="block text-purple-700 dark:text-purple-300 text-xs font-medium mt-1 max-w-[180px] truncate hover:underline focus:outline-none focus:underline cursor-pointer text-left"
                            title={`View ${student.agent_company_name}'s profile`}
                            data-testid={`student-agent-link-${student.id}`}
                          >
                            {student.agent_company_name}
                          </button>
                        ) : (
                          <p className="text-gray-700 dark:text-gray-200 text-xs font-medium mt-1 max-w-[180px] truncate" title={student.agent_company_name || student.referral_source_name || ''}>
                            {student.agent_company_name || student.referral_source_name || 'Unknown agency'}
                          </p>
                        )}
                      </div>
                    ) : student.source ? (
                      <div data-testid={`student-source-direct-${student.id}`}>
                        <Badge className={
                          student.source === 'referral' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                          student.source === 'social_media' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                          student.source === 'walk_in' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                          'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }>
                          {student.source.replace('_', ' ')}
                        </Badge>
                        {student.source_detail && (
                          <p className="text-gray-400 text-xs mt-1">{student.source_detail}</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs" data-testid={`student-source-direct-${student.id}`}>Self-registered</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Badge className="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                      {student.application_count || 0} apps
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <Badge className={student.note_count > 0 ? 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}>
                      <StickyNote className="w-3 h-3 mr-1" />
                      {student.note_count || 0}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-gray-500 dark:text-gray-400 text-sm">
                    {new Date(student.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => onViewStudent(student)}
                        className="bg-teal-600/10 text-teal-600 hover:bg-teal-600/20 dark:bg-teal-500/20 dark:text-teal-400"
                        data-testid={`view-student-${student.id}`}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleOpenApplyDialog(student)}
                        className="bg-blue-600/10 text-blue-600 hover:bg-blue-600/20 dark:bg-blue-500/20 dark:text-blue-400"
                        data-testid={`apply-for-student-${student.id}`}
                      >
                        <GraduationCap className="w-3 h-3 mr-1" />
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openPaymentDialog(student)}
                        className="bg-emerald-600/10 text-emerald-600 hover:bg-emerald-600/20 dark:bg-emerald-500/20 dark:text-emerald-400"
                        data-testid={`record-payment-${student.id}`}
                        title="Record a payment for this student"
                      >
                        <DollarSign className="w-3 h-3 mr-1" />
                        Payment
                      </Button>
                      {isSuperAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openDeleteConfirm(student)}
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          data-testid={`delete-student-${student.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Student Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Student
            </DialogTitle>
          </DialogHeader>
          {deletingStudent && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Are you sure you want to permanently delete <strong>{deletingStudent.full_name}</strong>?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Warning:</strong> This will delete all their applications, documents, and profile data. This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 border-gray-300 dark:border-slate-600 dark:text-gray-300"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteStudent}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeleting}
                  data-testid="confirm-delete-student"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Student
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply for Course Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white text-xl flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-teal-600" />
              Apply for Course
              {selectedStudentForApply && (
                <span className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                  - {selectedStudentForApply.full_name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Course Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search courses by name, university, or field..."
                value={courseSearchTerm}
                onChange={(e) => setCourseSearchTerm(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white"
                data-testid="course-search-input"
              />
            </div>
            
            {/* Courses List */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {searchingCourses ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                  Searching courses...
                </div>
              ) : filteredCourses.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {courseSearchTerm ? 'No courses found' : 'Type to search from 13,000+ courses'}
                </div>
              ) : (
                filteredCourses.map((course) => (
                  <div
                    key={course.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700/50"
                    data-testid={`course-option-${course.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 dark:text-white truncate">
                        {course.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {course.university_name} • {course.country}
                      </p>
                      <div className="flex gap-2 mt-1">
                        {course.degree_level && (
                          <Badge className="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 text-xs">
                            {course.degree_level}
                          </Badge>
                        )}
                        {course.field_of_study && (
                          <Badge className="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 text-xs">
                            {course.field_of_study}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleApplyForStudent(course.id)}
                      disabled={applyingForCourse === course.id}
                      className="ml-3 bg-teal-600 text-white hover:bg-teal-700"
                      data-testid={`apply-course-${course.id}`}
                    >
                      {applyingForCourse === course.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <BookOpen className="w-3 h-3 mr-1" />
                          Apply
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowApplyDialog(false);
                  setSelectedStudentForApply(null);
                  setCourseSearchTerm('');
                }}
                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={showAddStudent} onOpenChange={setShowAddStudent}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-600" />
              Add New Student
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStudent} className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-gray-600 dark:text-gray-300">Full Name *</Label>
                <Input
                  value={newStudent.full_name}
                  onChange={(e) => setNewStudent({...newStudent, full_name: e.target.value})}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                  placeholder="Student's full name"
                  required
                  data-testid="add-student-name"
                />
              </div>
              <div>
                <Label className="text-gray-600 dark:text-gray-300">Email *</Label>
                <Input
                  type="email"
                  value={newStudent.email}
                  onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                  placeholder="student@email.com"
                  required
                  data-testid="add-student-email"
                />
              </div>
              <div>
                <Label className="text-gray-600 dark:text-gray-300">Phone</Label>
                <Input
                  value={newStudent.phone}
                  onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                  placeholder="+92 300 1234567"
                  data-testid="add-student-phone"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-600 dark:text-gray-300">Source *</Label>
                <Select
                  value={newStudent.source}
                  onValueChange={(value) => setNewStudent({...newStudent, source: value})}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1" data-testid="add-student-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600">
                    <SelectItem value="referral" className="text-gray-800 dark:text-white">Referral</SelectItem>
                    <SelectItem value="social_media" className="text-gray-800 dark:text-white">Social Media</SelectItem>
                    <SelectItem value="walk_in" className="text-gray-800 dark:text-white">Walk-in</SelectItem>
                    <SelectItem value="website" className="text-gray-800 dark:text-white">Website Inquiry</SelectItem>
                    <SelectItem value="other" className="text-gray-800 dark:text-white">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-600 dark:text-gray-300">Source Detail</Label>
                <Input
                  value={newStudent.source_detail}
                  onChange={(e) => setNewStudent({...newStudent, source_detail: e.target.value})}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                  placeholder={
                    newStudent.source === 'referral' ? 'Referred by (name)' :
                    newStudent.source === 'social_media' ? 'Facebook, Instagram...' :
                    'Details...'
                  }
                  data-testid="add-student-source-detail"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-600 dark:text-gray-300">Initial Notes</Label>
              <Textarea
                value={newStudent.notes}
                onChange={(e) => setNewStudent({...newStudent, notes: e.target.value})}
                className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-800 dark:text-white mt-1"
                placeholder="Any additional notes about this student..."
                rows={3}
                data-testid="add-student-notes"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddStudent(false)}
                className="flex-1 border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={addingStudent}
                className="flex-1 bg-teal-600 text-white hover:bg-teal-700"
                data-testid="submit-add-student"
              >
                {addingStudent ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Student
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Phase C — Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Record Payment {paymentStudent ? `— ${paymentStudent.full_name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (£) *</Label>
                <Input type="number" min="0" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="payment-amount" />
              </div>
              <div>
                <Label className="text-xs">Payment Type *</Label>
                <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_type: v })}>
                  <SelectTrigger data-testid="payment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tuition">Tuition Fee</SelectItem>
                    <SelectItem value="visa_fee">Visa Fee</SelectItem>
                    <SelectItem value="accommodation">Accommodation</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="service_charge">Service Charge</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payment Date</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} data-testid="payment-date" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={paymentForm.status} onValueChange={(v) => setPaymentForm({ ...paymentForm, status: v })}>
                  <SelectTrigger data-testid="payment-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description *</Label>
              <Input placeholder="e.g. First semester tuition Jan 2026 intake" value={paymentForm.description} onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })} data-testid="payment-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Reference Number</Label>
                <Input placeholder="Bank ref / receipt #" value={paymentForm.reference_number} onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} data-testid="payment-ref" />
              </div>
              <div>
                <Label className="text-xs">Internal Notes</Label>
                <Input value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} data-testid="payment-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Close</Button>
              <Button onClick={savePayment} disabled={savingPayment} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-payment-btn">
                {savingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <DollarSign className="w-4 h-4 mr-1" />}
                Record Payment
              </Button>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recent Payments</h4>
              {paymentHistory.length === 0 ? (
                <p className="text-xs text-gray-500">No payments recorded yet.</p>
              ) : (
                <div className="border border-gray-200 dark:border-slate-700 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                      <tr>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-right px-3 py-2">Amount</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Recorded by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentHistory.map((p) => (
                        <tr key={p.id} className="border-t border-gray-100 dark:border-slate-700">
                          <td className="px-3 py-2">{p.payment_date}</td>
                          <td className="px-3 py-2 capitalize">{(p.payment_type || '').replace('_', ' ')}</td>
                          <td className="px-3 py-2 text-right font-medium">£{Number(p.amount || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 capitalize">{p.status}</td>
                          <td className="px-3 py-2">{p.recorded_by_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentsTab;
